import React, { createContext, useState, FunctionComponent, useContext, useEffect } from 'react';
import { useStripe } from '@stripe/react-stripe-js';
import type { PaymentRequest, CanMakePaymentResult } from '@stripe/stripe-js';
import useConfig from '../../../common/providers/configProvider/use-config';

type WalletType = 'unknown' | 'applePay' | 'googlePay' | null;

export interface WalletProviderContext {
  paymentRequest: PaymentRequest | null;
  walletType: WalletType;
  startWalletPayment: () => void;
}

const defaultStartWalletPayment = () => {};

const context = createContext<WalletProviderContext>({
  paymentRequest: null,
  walletType: 'unknown',
  startWalletPayment: defaultStartWalletPayment,
});

const { Provider } = context;

const parseWalletType = (supportedWalletTypes: CanMakePaymentResult | null) => {
  if (supportedWalletTypes === null) return null;
  if (supportedWalletTypes.applePay) return 'applePay';
  if (supportedWalletTypes.googlePay) return 'googlePay';
  return null;
};

const WalletProvider: FunctionComponent = ({ children }) => {
  const stripe = useStripe();
  const { content, paymentMode } = useConfig();

  const [paymentRequest, setPaymentRequest] = useState<PaymentRequest | null>(null);
  const [walletType, setWalletType] = useState<WalletType>('unknown');

  useEffect(() => {
    if (stripe && !paymentRequest && content) {
      const {
        common: { currency, countryCode },
      } = content;

      const determineWalletSupport = async (generatedPaymentRequest: PaymentRequest) => {
        const supportedWalletTypes = await generatedPaymentRequest.canMakePayment();
        setWalletType(paymentMode ? null : parseWalletType(supportedWalletTypes));
      };

      const generatedPaymentRequest = stripe.paymentRequest({
        country: countryCode.toUpperCase(),
        currency: currency.toLowerCase(),
        total: {
          // Users should never see this label
          label: 'Something is wrong - please try again',
          amount: 0,
          pending: true,
        },
        requestPayerName: true,
        requestPayerEmail: true,
      });

      determineWalletSupport(generatedPaymentRequest);
      setPaymentRequest(generatedPaymentRequest);
    }
  }, [stripe, paymentRequest, content, paymentMode]);

  const startWalletPayment = paymentRequest ? paymentRequest.show : defaultStartWalletPayment;

  return <Provider value={{ paymentRequest, walletType, startWalletPayment }}>{children}</Provider>;
};

const useWallet = () => useContext(context);

export { WalletProvider, useWallet };
